import { useApolloClient, useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useState } from "react";
import { refetchWasteStreamGridQueries } from "routes/Customer/Waste/refetchWasteStreamGridQueries";

import { UNLINK_WASTE_STREAM_FROM_REGISTRATION } from "./api";

export type RemoveRegistrationLinkDialogProps = {
  open: boolean;
  onClose: () => void;
  wasteStream: { id: string; name: string };
  registration: { stateRegisteredId?: string | null; name?: string | null };
  /** The stream's facility (a link never crosses facilities); also scopes the grid refetch. */
  facility: { id: string; name?: string | null } | null | undefined;
  /** The opening surface's own queries, refetched alongside the waste stream grids. */
  refetchQueries?: string[];
};

function registrationLabel(registration: RemoveRegistrationLinkDialogProps["registration"]) {
  return [registration.stateRegisteredId, registration.name].filter(Boolean).join(" · ") || "—";
}

/**
 * Staff confirm for undoing a stream↔registration link — shared by the
 * registration drawer's linked-stream list and the waste stream drawer. Only
 * the link is removed; the stream and the registration both remain.
 */
export function RemoveRegistrationLinkDialog({
  open,
  onClose,
  wasteStream,
  registration,
  facility,
  refetchQueries = [],
}: RemoveRegistrationLinkDialogProps) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const [unlink] = useMutation(UNLINK_WASTE_STREAM_FROM_REGISTRATION);
  // Covers the refetch as well as the mutation, so confirm can't be clicked
  // again while the surfaces behind the dialog are still reloading.
  const [removing, setRemoving] = useState(false);

  const handleConfirm = async () => {
    setRemoving(true);
    try {
      try {
        await unlink({ variables: { wasteStreamId: wasteStream.id } });
      } catch (e) {
        alerts.error("Failed to remove the waste stream from the registration.", e);
        return;
      }
      await refetchWasteStreamGridQueries(client, facility?.id, refetchQueries);
      alerts.success("Waste stream removed from the registration.");
      onClose();
    } finally {
      setRemoving(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      loading={removing}
      header="Remove this waste stream from the registration?"
      resources={[
        { resourceType: "Facility", resourceName: facility?.name || "—" },
        { resourceType: "Waste Stream", resourceName: wasteStream.name },
        { resourceType: "Registration", resourceName: registrationLabel(registration) },
      ]}
      info="The registration will no longer be on this waste stream. The waste stream is not deleted, and the registration itself is unchanged."
      confirmText="Yes, Remove"
    />
  );
}
